import React, { useEffect, useState } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  Bus, 
  DollarSign, 
  Filter, 
  Search, 
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageCircle,
  FileText,
  Star
} from 'lucide-react';
import { InventoryScheduleDocument } from '../../types';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

export const SlotsPage: React.FC = () => {
  const [slots, setSlots] = useState<InventoryScheduleDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startTime: '',
    endTime: '',
    oldBookings: false,
    assignment: false,
    all: false,
    inventoryId: '',
    coordinators: false,
    hideCancelled: false
  });
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadSlots();
  }, []);

  const loadSlots = async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      
      if (filters.startTime) {
        params.startTime = new Date(filters.startTime).getTime();
      }
      if (filters.endTime) {
        params.endTime = new Date(filters.endTime).getTime();
      }
      if (filters.inventoryId) {
        params.inventoryId = filters.inventoryId;
      }
      
      // Add boolean filters only if they're true
      if (filters.oldBookings) params.oldBookings = true;
      if (filters.assignment) params.assignment = true;
      if (filters.all) params.all = true;
      if (filters.coordinators) params.coordinators = true;
      if (filters.hideCancelled) params.hideCancelled = true;

      const slotsData = await apiService.getSlots(params);
      setSlots(slotsData);
    } catch (error: any) {
      console.error('Failed to load slots:', error);
      toast.error(error.message || 'Failed to load slots');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatPrice = (price: number) => {
    return `₹${price}`;
  };

  const getSlotStatus = (slot: InventoryScheduleDocument) => {
    if (!slot.active) return 'Inactive';
    if (slot.currentBookingCount >= slot.maxSlots) return 'Full';
    if (slot.currentBookingCount > 0) return 'Partially Booked';
    return 'Available';
  };

  const getStatusColor = (slot: InventoryScheduleDocument) => {
    const status = getSlotStatus(slot);
    switch (status) {
      case 'Available': return 'text-green-600 bg-green-100';
      case 'Partially Booked': return 'text-yellow-600 bg-yellow-100';
      case 'Full': return 'text-red-600 bg-red-100';
      case 'Inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const toggleSlotExpansion = (slotId: string) => {
    const newExpanded = new Set(expandedSlots);
    if (newExpanded.has(slotId)) {
      newExpanded.delete(slotId);
    } else {
      newExpanded.add(slotId);
    }
    setExpandedSlots(newExpanded);
  };

  const filteredSlots = slots.filter(slot =>
    slot.tripTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    slot.displayTime.toLowerCase().includes(searchQuery.toLowerCase()) ||
    slot.route.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Trip Slots</h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Toggle filters"
            >
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search trips, times, or routes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startTime}
                  onChange={(e) => setFilters({...filters, startTime: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endTime}
                  onChange={(e) => setFilters({...filters, endTime: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inventory ID</label>
                <input
                  type="text"
                  placeholder="Enter inventory ID"
                  value={filters.inventoryId}
                  onChange={(e) => setFilters({...filters, inventoryId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.oldBookings}
                  onChange={(e) => setFilters({...filters, oldBookings: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Old Bookings</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.assignment}
                  onChange={(e) => setFilters({...filters, assignment: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Assignment</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.all}
                  onChange={(e) => setFilters({...filters, all: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">All</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.coordinators}
                  onChange={(e) => setFilters({...filters, coordinators: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Coordinators</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.hideCancelled}
                  onChange={(e) => setFilters({...filters, hideCancelled: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Hide Cancelled</span>
              </label>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={loadSlots}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Loading slots...</p>
            </div>
          </div>
        ) : filteredSlots.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Calendar className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              {searchQuery ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No matching slots</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">No slots match "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                  >
                    Clear search
                  </button>
                </>
              ) : slots.length === 0 ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No slots available</h3>
                  <p className="text-gray-500 dark:text-gray-400">Try applying different date filters to find slots</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No matching slots</h3>
                  <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filters</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSlots.map((slot) => (
              <div key={slot.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{slot.tripTitle}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(slot)}`}>
                          {getSlotStatus(slot)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>{formatDate(slot.startTime)}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-2" />
                          <span>{slot.displayTime}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <DollarSign className="h-4 w-4 mr-2" />
                          <span>{formatPrice(slot.price)}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-2" />
                          <span>{slot.currentBookingCount}/{slot.maxSlots} slots</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="h-4 w-4 mr-2" />
                          <span>{slot.route || 'Route not specified'}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Bus className="h-4 w-4 mr-2" />
                          <span>{slot.busType || 'Bus type not specified'}</span>
                        </div>
                      </div>

                      {slot.subText && (
                        <p className="text-sm text-gray-600 mb-3">{slot.subText}</p>
                      )}
                    </div>

                    <button
                      onClick={() => toggleSlotExpansion(slot.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label="Toggle details"
                    >
                      {expandedSlots.has(slot.id) ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedSlots.has(slot.id) && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Trip Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Trip Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-600">Inventory ID:</span>
                            <span className="ml-2 font-medium">{slot.inventoryId}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Start Time:</span>
                            <span className="ml-2">{formatTime(slot.startTime)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">End Time:</span>
                            <span className="ml-2">{formatTime(slot.endTime)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Additional Bookings:</span>
                            <span className="ml-2">{slot.additionalBookings}</span>
                          </div>
                          {slot.partialPaymentAllowed && (
                            <div>
                              <span className="text-gray-600">Partial Payment:</span>
                              <span className="ml-2">{formatPrice(slot.partialPaymentAmount)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Financial Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Financial Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-600">Total Amount:</span>
                            <span className="ml-2 font-medium">{formatPrice(slot.amountTotal)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total Expenses:</span>
                            <span className="ml-2">{formatPrice(slot.expenseTotal)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">NPS Score:</span>
                            <span className="ml-2 flex items-center">
                              <Star className="h-4 w-4 text-yellow-500 mr-1" />
                              {slot.npsScore} ({slot.npsCount} reviews)
                            </span>
                          </div>
                          {slot.pendingPaymentCount && (
                            <div>
                              <span className="text-gray-600">Pending Payments:</span>
                              <span className="ml-2">{slot.pendingPaymentCount}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Links and Actions */}
                      <div className="md:col-span-2">
                        <h4 className="font-medium text-gray-900 mb-3">Links & Actions</h4>
                        <div className="flex flex-wrap gap-2">
                          {slot.driveLink && (
                            <a
                              href={slot.driveLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Drive Link
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                          {slot.billLink && (
                            <a
                              href={slot.billLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm hover:bg-green-200 transition-colors"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Bill Link
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                          {slot.whatsAppLink && (
                            <a
                              href={slot.whatsAppLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm hover:bg-green-200 transition-colors"
                            >
                              <MessageCircle className="h-4 w-4 mr-1" />
                              WhatsApp Group
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Coordinators */}
                      {slot.coordinators && slot.coordinators.length > 0 && (
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-3">Coordinators</h4>
                          <div className="space-y-2">
                            {slot.coordinators.map((coordinator, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div>
                                  <span className="font-medium">{coordinator.coordinatorName}</span>
                                  <span className="text-sm text-gray-600 ml-2">({coordinator.coordinatorPhone})</span>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  coordinator.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {coordinator.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {slot.notes && (
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-3">Notes</h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border">{slot.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
